import React, { useState, useEffect, useMemo } from 'react'
import { Box, Typography, IconButton, Grid } from '@the-platform-group/component-library'
import { StepperContainer } from '@the-platform-group/component-library/Stepper'
import {
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
} from '@the-platform-group/component-library/Icons'
import { Button } from '@the-platform-group/component-library/Button'
import { Select } from '@the-platform-group/component-library/Select'
import { usePeopleMergeContext } from 'pages/MergeProfiles/contexts/PeopleMergeContext'
import {
  getJobTitleAndOrgName,
  getJobFunctionLabel,
  getJobFieldValue,
} from 'pages/MergeProfiles/utils'
import { getMergedOverridesForEdit } from './utils'
import JobOverrideTable from './JobOverrideTable'
import BaseJobDrawer from './BaseJobDrawer'

const STEPS = [
  { label: 'Map job', key: 'mapJob' },
  { label: 'Job override', key: 'jobOverride' },
]

const JobLabelStacked = ({ job }) => (
  <>
    <Grid item xs={12}>
      <Typography variant="body2" fontWeight="medium">
        {getJobFunctionLabel(job) || '-'}
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'wrap' }}>
        {job.organization?.name || '-'}
      </Typography>
    </Grid>
  </>
)

/**
 * JobMapDrawer - Main interface for creating and editing job mappings
 * This is the primary drawer for mapping Platform jobs to FactSet jobs with field overrides.
 * It uses a 2-step wizard:
 *
 * **Step 1: Map Job**
 * - Select which FactSet job to map the Platform job(s) to
 * - Prevents mapping to already-mapped FactSet jobs
 *
 * **Step 2: Job Override**
 * - Select which source (Platform job or FactSet job) to use for each field
 * - Fields: Email, Phone, Job Status
 * - Uses radio buttons to choose between available values
 *
 * **Opened by:**
 * - Clicking "Map" button on selected Platform jobs in PlatformJobsList
 * - Clicking "Edit Job" in JobMappedPreviewDrawer (for existing mappings)
 *
 * **Edit Mode:**
 * When editing an existing mapping, the drawer:
 * - Pre-selects the previously chosen FactSet job
 * - Reconstructs the override selections from saved values
 * - Updates the mapping
 *
 * @param {Object} props
 * @param {boolean} props.open - Controls drawer visibility
 * @param {Function} props.onClose - Callback to close the drawer
 * @param {Object} props.selectedJobs - Selected Platform jobs to map
 * @param {string[]} props.selectedJobs.sourceJobIds - Array of Platform job IDs
 * @param {Function} props.onSave - Callback after successful save (optional)
 *
 * @returns {React.Component} The job mapping drawer with 2-step wizard
 */
const JobMapDrawer = ({ open, onClose, selectedJobs, onSave }) => {
  const { mergeData, jobMappingData } = usePeopleMergeContext()
  const { sourceJobs, targetJobs } = mergeData
  const {
    selectedPlatformJobs,
    addMapping,
    updateMapping,
    getMappingBySourceId,
    clearSelection,
    toggleJobSelection,
    mappedTargetJobIds,
  } = jobMappingData

  const [activeStep, setActiveStep] = useState(0)
  const [selectedFactSetJobId, setSelectedFactSetJobId] = useState(null)
  const [overrideValues, setOverrideValues] = useState({})

  const isEditMode = !!selectedJobs

  const selectedSourceJobs = useMemo(() => {
    if (isEditMode) {
      return sourceJobs.filter(job => selectedJobs.sourceJobIds.includes(job.id))
    }
    return sourceJobs.filter(job => selectedPlatformJobs.includes(job.id))
  }, [isEditMode, selectedJobs, sourceJobs, selectedPlatformJobs])

  const firstSourceJobId = selectedJobs?.sourceJobIds?.[0]

  const factSetJobOptions = useMemo(() => {
    const currentTargetId = isEditMode ? getMappingBySourceId(firstSourceJobId)?.targetId : null

    return targetJobs.map(job => {
      const isAlreadyMapped = mappedTargetJobIds.includes(job.id)
      const isCurrentEditTarget = job.id === currentTargetId

      return {
        value: job.id,
        label: getJobTitleAndOrgName(job),
        ...(job.isPrimary && {
          action: (
            <Typography color="text.default" pl={1}>
              Primary
            </Typography>
          ),
        }),
        ...(job.active === false && {
          secondaryText: 'Inactive',
        }),
        // Disable if already mapped, unless it's the current target in edit mode
        disabled: isAlreadyMapped && !isCurrentEditTarget,
      }
    })
  }, [targetJobs, mappedTargetJobIds, isEditMode, firstSourceJobId, getMappingBySourceId])

  const selectedFactSetJob = useMemo(() => {
    return targetJobs.find(job => job.id === selectedFactSetJobId)
  }, [targetJobs, selectedFactSetJobId])

  useEffect(() => {
    if (open) {
      if (isEditMode) {
        const existingMapping = getMappingBySourceId(firstSourceJobId)
        if (existingMapping) {
          setSelectedFactSetJobId(existingMapping.targetId)
          // Reconstruct which source job was selected for each override field
          const mergedOverrides = getMergedOverridesForEdit(
            selectedJobs.sourceJobIds,
            sourceJobs,
            getMappingBySourceId,
          )
          setOverrideValues(mergedOverrides)
          setActiveStep(1)
        }
      } else {
        setActiveStep(0)
        setOverrideValues({})
        // Auto-select if there's only one unmapped FactSet job
        const unmappedTargetJobs = targetJobs.filter(job => !mappedTargetJobIds.includes(job.id))
        if (unmappedTargetJobs.length === 1) {
          setSelectedFactSetJobId(unmappedTargetJobs[0].id)
        } else {
          setSelectedFactSetJobId(null)
        }
      }
    }
  }, [
    open,
    isEditMode,
    selectedJobs,
    getMappingBySourceId,
    targetJobs,
    mappedTargetJobIds,
    firstSourceJobId,
    sourceJobs,
  ])

  const handleFactSetJobChange = e => {
    setSelectedFactSetJobId(e.target.value || null)
  }

  const handleOverrideChange = (fieldKey, sourceJobId) => {
    setOverrideValues(prev => ({
      ...prev,
      [fieldKey]: sourceJobId,
    }))
  }

  const handleNext = () => {
    if (activeStep === 0 && selectedFactSetJobId) {
      setActiveStep(1)
    }
  }

  const handleBack = () => {
    if (activeStep === 1) {
      setActiveStep(0)
    }
  }

  const handleComplete = () => {
    if (!selectedFactSetJobId) {
      return
    }

    const mappingOverrides = {}
    selectedSourceJobs.forEach(job => {
      const jobOverrides = {}
      Object.entries(overrideValues).forEach(([fieldKey, selectedSourceId]) => {
        if (selectedSourceId !== 'factset' && selectedSourceId === job.id) {
          jobOverrides[fieldKey] = getJobFieldValue(job, fieldKey, { defaultValue: null })
        }
      })
      mappingOverrides[job.id] = jobOverrides
    })

    if (isEditMode) {
      // Update all mapped source jobs, not just the first one
      selectedSourceJobs.forEach(job => {
        updateMapping(job.id, selectedFactSetJobId, mappingOverrides[job.id] || {})
      })
    } else {
      addMapping(
        selectedSourceJobs.map(j => j.id),
        selectedFactSetJobId,
        mappingOverrides,
      )
    }

    clearSelection()
    onClose()
    onSave()
  }

  const handleClose = () => {
    setActiveStep(0)
    setSelectedFactSetJobId(null)
    setOverrideValues({})
    onClose()
  }

  return (
    <BaseJobDrawer open={open} onClose={handleClose} title="Map Job">
      <Grid container spacing={4}>
        <Grid item container spacing={2} alignItems="flex-start">
          <Grid item container xs={5} spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Platform Jobs Selected
              </Typography>
            </Grid>
            {selectedSourceJobs.map(job => {
              const canRemoveJob = !isEditMode && selectedSourceJobs.length > 1
              return (
                <Grid item container key={job.id} alignItems="flex-start">
                  {canRemoveJob && (
                    <Grid item xs={2}>
                      <IconButton size="small" onClick={() => toggleJobSelection(job.id)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Grid>
                  )}
                  <Grid item container xs={canRemoveJob ? 10 : 12} alignItems="center">
                    <JobLabelStacked job={job} />
                  </Grid>
                </Grid>
              )
            })}
          </Grid>

          <Grid item xs={2} justifyContent="center" alignItems="flex-start" pt={4}>
            <ArrowForwardIcon color="action" />
          </Grid>

          <Grid item container xs={5} spacing={1} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                FactSet Job to be Mapped
              </Typography>
            </Grid>
            {selectedFactSetJob ? (
              <Grid item container direction="column">
                <JobLabelStacked job={selectedFactSetJob} />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Not selected
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item container justifyContent="center">
          <Box sx={{ width: '100%' }}>
            <StepperContainer steps={STEPS} activeStep={activeStep} />
          </Box>
        </Grid>

        {activeStep === 0 && (
          <Grid item container spacing={2} alignItems="flex-start">
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                FactSet job to be mapped
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Select
                fullWidth
                name="factSetJob"
                label="Select a FactSet job"
                options={factSetJobOptions}
                value={selectedFactSetJobId || ''}
                onChange={handleFactSetJobChange}
              />
            </Grid>
          </Grid>
        )}

        {activeStep === 1 && selectedFactSetJob && (
          <Grid item container>
            <JobOverrideTable
              platformJobs={selectedSourceJobs}
              factSetJob={selectedFactSetJob}
              overrideValues={overrideValues}
              onOverrideChange={handleOverrideChange}
            />
          </Grid>
        )}

        <Grid item container justifyContent="space-between">
          <Grid item>
            <Button variant="outlined" onClick={activeStep === 0 ? handleClose : handleBack}>
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={activeStep === 0 ? handleNext : handleComplete}
              disabled={activeStep === 0 && !selectedFactSetJobId}
            >
              {activeStep === 0 ? 'Next' : 'Complete'}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </BaseJobDrawer>
  )
}

export default JobMapDrawer
